/**
 * Слайс профиля: состояние, редьюсер, селекторы (без UI).
 *
 * @module store/profile
 */

import { createSelector } from 'reselect';

import { FetchError } from '../../requests/sendRequest';
import type { RootAction } from '../store.types';
import type { MetaState } from '../store.utils';
import { setMeta } from '../store.utils';

import {
  FETCH_PROFILE_ERROR,
  FETCH_PROFILE_START,
  FETCH_PROFILE_SUCCESS,
  PROFILE_FETCH_ERROR_KEY,
  SET_PROFILE_FULFILL,
} from './actionTypes';
import type { SurveyUserProfile } from './userProfile.types';

/** Состояние слайса профиля */
export interface ProfileState {
  profile: SurveyUserProfile | null;
  /** Флаг заполненности из Keycloak (для будущих guard); null — ещё неизвестно */
  profileFulfill: boolean | null;
  meta: MetaState;
}

export const initialProfileState: ProfileState = {
  profile: null,
  profileFulfill: null,
  meta: setMeta.initial(),
};

/**
 * Возвращает первую букву строки в верхнем регистре.
 *
 * @param {string | null} value - исходная строка
 * @returns {string}
 */
function takeInitial(value: string | null): string {
  if (typeof value !== 'string' || value === '') {
    return '';
  }
  return value[0].toUpperCase();
}

/**
 * Собирает инициалы пользователя из имени/фамилии либо из username/email как fallback.
 *
 * @param {SurveyUserProfile | null} profile - профиль из store
 * @returns {string | null}
 */
function buildProfileInitials(profile: SurveyUserProfile | null): string | null {
  if (profile === null) {
    return null;
  }

  const firstNameInitial = takeInitial(profile.firstName);
  const lastNameInitial = takeInitial(profile.lastName);
  const initials = `${firstNameInitial}${lastNameInitial}`;
  if (initials !== '') {
    return initials;
  }

  const usernameInitial = takeInitial(profile.username);
  if (usernameInitial !== '') {
    return usernameInitial;
  }

  const emailInitial = takeInitial(profile.email);
  if (emailInitial !== '') {
    return emailInitial;
  }

  return null;
}

/**
 * Редьюсер профиля; чужие корневые экшены не изменяют слайс.
 *
 * @param {ProfileState} state — текущее состояние
 * @param {RootAction} action — корневой экшен
 * @returns {ProfileState}
 */
export function profileReducer(state: ProfileState, action: RootAction): ProfileState {
  if (action.type === FETCH_PROFILE_START) {
    return { ...state, meta: setMeta.pending() };
  }
  if (action.type === FETCH_PROFILE_SUCCESS) {
    return {
      ...state,
      profile: action.payload,
      meta: setMeta.success(),
    };
  }
  if (action.type === FETCH_PROFILE_ERROR) {
    return { ...state, meta: setMeta.error(action.payload) };
  }
  if (action.type === SET_PROFILE_FULFILL) {
    return {
      ...state,
      profileFulfill: action.payload,
    };
  }
  return state;
}

export const selectProfileMeta = (state: ProfileState) => state.meta;

export const selectProfileData = (state: ProfileState) => state.profile;

export const selectProfileFulfill = (state: ProfileState) => state.profileFulfill;

export const selectFetchProfileSuccess = createSelector(
  [selectProfileMeta],
  (meta) => !meta.pending && (meta.success || Object.keys(meta.errors).length > 0),
);

/**
 * Возвращает `true`, только если профиль действительно пришёл и meta помечена как success.
 */
export const selectProfileReady = createSelector(
  [selectProfileMeta, selectProfileData],
  (meta, profile): boolean => meta.success && profile !== null,
);

/**
 * Вычисляет инициалы пользователя для avatar/header блока.
 */
export const selectProfileInitials = createSelector([selectProfileData], (profile): string | null =>
  buildProfileInitials(profile),
);

/**
 * null — профиль не загружен или загрузка не завершена;
 * true — есть числовой deleteRequestTimestamp;
 * false — профиль есть, удаление не запрошено.
 */
export const selectProfileIsDeleted = createSelector(
  [selectFetchProfileSuccess, selectProfileData],
  (fetchSuccess, profile): boolean | null => {
    if (!fetchSuccess || profile === null) {
      return null;
    }
    if (!Object.hasOwn(profile, 'deleteRequestTimestamp')) {
      return false;
    }
    const value = profile.deleteRequestTimestamp;
    return typeof value === 'number' ? true : false;
  },
);

export const selectProfileDeleteRequestTimestamp = createSelector(
  [selectProfileIsDeleted, selectProfileData],
  (isDeleted, profile): number | null => {
    if (isDeleted !== true || profile === null) {
      return null;
    }
    if (!Object.hasOwn(profile, 'deleteRequestTimestamp')) {
      return null;
    }
    const value = profile.deleteRequestTimestamp;
    return typeof value === 'number' ? value : null;
  },
);

export const selectProfileLoading = createSelector([selectProfileMeta], (meta) => meta.pending);

export const selectProfileError = createSelector([selectProfileMeta], (meta): FetchError | null => {
  if (!Object.hasOwn(meta.errors, PROFILE_FETCH_ERROR_KEY)) {
    return null;
  }
  const candidate = meta.errors[PROFILE_FETCH_ERROR_KEY];
  if (candidate instanceof FetchError) {
    return candidate;
  }
  return null;
});

export const selectProfileFetchStatuses = createSelector([selectProfileMeta], (meta) => ({
  pending: meta.pending,
  success: meta.success,
  error: Object.keys(meta.errors).length > 0,
}));

export {
  FETCH_PROFILE_ERROR,
  FETCH_PROFILE_START,
  FETCH_PROFILE_SUCCESS,
  PROFILE_FETCH_ERROR_KEY,
  SET_PROFILE_FULFILL,
} from './actionTypes';
export type { ProfileAction } from './actionTypes';
